import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { addPrefixPath } from '@serpro/volto-prefixpath/helpers/Url/Url';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import BRSelect from '../../Select/BRSelect';

import styles from './LinkMapBody.module.less';
import Map from './Map';
import { BRAZIL_STATES, getStateFieldName, STATE_PROP_TYPES } from './states';

const messages = defineMessages({
  mapAltWithStates: {
    id: 'linkMapAccessibleLabelWithStates',
    defaultMessage:
      'Brazil map used as a visual reference for the linked states.',
  },
  mapAltNoStates: {
    id: 'linkMapAccessibleLabelWithoutStates',
    defaultMessage:
      'Brazil map used as a visual reference. No linked states configured.',
  },
  linkedStatesIntro: {
    id: 'linkMapLinkedStatesIntro',
    defaultMessage: 'Linked states:',
  },
  selectPlaceholder: {
    id: 'linkMapSelectPlaceholder',
    defaultMessage: 'Select a state',
  },
  subtitlePlaceholder: {
    id: 'linkMapSubtitlePlaceholder',
    defaultMessage: 'Add a subtitle for the map',
  },
  emptyStateMessage: {
    id: 'linkMapEmptyStateMessage',
    defaultMessage: 'No linked states have been configured.',
  },
});

const ALLOWED_LINK_PREFIXES = ['http://', 'https://', 'mailto:', 'tel:'];

const sanitizeLinkValue = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = addPrefixPath(value.trim());

  if (!trimmed) {
    return '';
  }

  const lower = trimmed.toLowerCase();

  if (trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return trimmed;
  }

  if (ALLOWED_LINK_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return trimmed;
  }

  return '';
};

const LINK_TEMPLATE_PLACEHOLDER_REGEX = /<<([uUfF]{2})>>/g;

const formatStateCodeForPlaceholder = (stateCode, pattern = '') => {
  if (!stateCode) {
    return '';
  }

  const normalizedCode = stateCode.toUpperCase();
  const patternChars = pattern.split('');

  return normalizedCode
    .split('')
    .map((letter, index) => {
      const patternChar =
        patternChars[index] ?? patternChars[patternChars.length - 1];
      if (!patternChar) {
        return letter;
      }

      return patternChar === patternChar.toUpperCase()
        ? letter.toUpperCase()
        : letter.toLowerCase();
    })
    .join('');
};

const applyLinkTemplate = (template, stateCode) => {
  if (!template) {
    return '';
  }

  let hasPlaceholder = false;

  const replacedTemplate = template.replace(
    LINK_TEMPLATE_PLACEHOLDER_REGEX,
    (match, pattern) => {
      hasPlaceholder = true;
      return formatStateCodeForPlaceholder(stateCode, pattern);
    },
  );

  return hasPlaceholder ? replacedTemplate : template;
};

const LinkMapBody = ({ data, isEditMode = false, placeholder }) => {
  const intl = useIntl();
  const linkFormat = data?.linkFormat || 'news-search';
  const isNewsSearchFormat = linkFormat === 'news-search';
  const title = data?.title?.trim();
  const hasTitle = Boolean(title);
  const titleToRender = title || placeholder;
  const subtitle = data?.subtitle?.trim();
  const hasSubtitle = Boolean(subtitle);
  const subtitlePlaceholder = intl.formatMessage(messages.subtitlePlaceholder);
  const subtitleToRender = hasSubtitle
    ? subtitle
    : isEditMode
      ? subtitlePlaceholder
      : '';
  const linkTemplate = useMemo(() => {
    if (isNewsSearchFormat || typeof data?.linkTemplate !== 'string') {
      return '';
    }

    const trimmed = data.linkTemplate.trim();
    return trimmed || '';
  }, [data?.linkTemplate, isNewsSearchFormat]);

  const localeSegment = (intl?.locale || 'pt-br')
    .toLowerCase()
    .replace(/_/g, '-');
  const newsSearchBasePath = useMemo(() => {
    if (!isNewsSearchFormat) {
      return '';
    }

    return addPrefixPath(`/${localeSegment}/listagem_noticias_por_uf`);
  }, [isNewsSearchFormat, localeSegment]);

  const localContextQueryValue = data?.searchAcrossPortal ? 'False' : 'True';
  const newsSearchLinks = useMemo(() => {
    if (!isNewsSearchFormat || !newsSearchBasePath) {
      return {};
    }

    return BRAZIL_STATES.reduce((acc, state) => {
      acc[state.code] =
        `${newsSearchBasePath}?local_context=${localContextQueryValue}&uf_noticia=${encodeURIComponent(
          state.code,
        )}`;
      return acc;
    }, {});
  }, [isNewsSearchFormat, localContextQueryValue, newsSearchBasePath]);

  const templatedLinks = useMemo(() => {
    if (!linkTemplate) {
      return {};
    }

    return BRAZIL_STATES.reduce((acc, state) => {
      const templatedLink = sanitizeLinkValue(
        applyLinkTemplate(linkTemplate, state.code),
      );

      if (templatedLink) {
        acc[state.code] = templatedLink;
      }

      return acc;
    }, {});
  }, [linkTemplate]);

  const explicitLinks = useMemo(() => {
    if (isNewsSearchFormat) {
      return {};
    }

    return BRAZIL_STATES.reduce((acc, state) => {
      const rawValue = data?.[getStateFieldName(state.code)];
      const directLink = sanitizeLinkValue(rawValue);

      if (directLink) {
        acc[state.code] = directLink;
      }

      return acc;
    }, {});
  }, [data, isNewsSearchFormat]);

  const stateLinks = useMemo(() => {
    return BRAZIL_STATES.reduce((acc, state) => {
      acc[state.code] =
        explicitLinks[state.code] ??
        templatedLinks[state.code] ??
        newsSearchLinks[state.code] ??
        '';
      return acc;
    }, {});
  }, [explicitLinks, templatedLinks, newsSearchLinks]);

  const linkedStates = useMemo(
    () => BRAZIL_STATES.filter((state) => Boolean(stateLinks[state.code])),
    [stateLinks],
  );

  const selectIdGenerator = useId || (() => null);
  const generatedSelectId = selectIdGenerator();
  const selectId = generatedSelectId || 'link-map-select';
  const [highlightedState, setHighlightedState] = useState(null);
  const [selectedStateCode, setSelectedStateCode] = useState('');

  const selectOptions = useMemo(
    () =>
      linkedStates.map((state) => ({
        value: state.code,
        label: state.region
          ? `${state.name} (${state.code}) - ${state.region}`
          : `${state.name} (${state.code})`,
      })),
    [linkedStates],
  );

  useEffect(() => {
    if (highlightedState && !stateLinks[highlightedState]) {
      setHighlightedState(null);
    }
  }, [highlightedState, stateLinks]);

  useEffect(() => {
    if (selectedStateCode && !stateLinks[selectedStateCode]) {
      setSelectedStateCode('');
    }
  }, [selectedStateCode, stateLinks]);

  const handleStateHover = useCallback((stateCode) => {
    setHighlightedState(stateCode);
  }, []);

  const handleStateLeave = useCallback(() => {
    setHighlightedState(null);
  }, []);

  const accessibleLabel = linkedStates.length
    ? intl.formatMessage(messages.mapAltWithStates)
    : intl.formatMessage(messages.mapAltNoStates);

  const fallbackAlt = accessibleLabel;
  const useDarkTheme = Boolean(data?.useDarkTheme);
  const useFullWidth = data?.useFullWidth !== false;
  const containerClasses = cx(
    'pt-0 pb-4 pt-md-5x pb-md-6x',
    useFullWidth ? 'full-width' : 'rounder-md px-2x px-md-6x',
    useDarkTheme ? 'bg-blue-warm-vivid-80' : 'bg-gray-cool-4',
    styles.linkMapRoot,
    {
      [styles.linkMapRootDark]: useDarkTheme,
      [styles.linkMapRootLight]: !useDarkTheme,
    },
  );

  const handleSelectChange = useCallback(
    (nextStateCode) => {
      const sanitizedValue =
        typeof nextStateCode === 'string' ? nextStateCode.trim() : '';

      setSelectedStateCode(sanitizedValue);

      if (!sanitizedValue) {
        handleStateLeave();
        return;
      }

      handleStateHover(sanitizedValue);

      const targetLink = stateLinks[sanitizedValue];

      if (!targetLink || isEditMode) {
        return;
      }

      if (typeof window !== 'undefined') {
        window.location.href = targetLink;
      }
    },
    [handleStateHover, handleStateLeave, isEditMode, stateLinks],
  );

  const shouldRenderSubtitle =
    hasSubtitle || (isEditMode && subtitlePlaceholder);

  return (
    <div className={containerClasses}>
      <div className="ui container">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-start gap-4">
          <div className="d-flex flex-column">
            {(hasTitle || (isEditMode && placeholder)) && (
              <h2
                className={cx(
                  'text-up-01 text-md-up-06 text-weight-bold mt-md-5 mb-2 mb-md-4',
                  {
                    'text-yellow-vivid-20': useDarkTheme,
                    'text-green-vivid-50': !useDarkTheme,
                    'opacity-5': isEditMode && !hasTitle,
                  },
                )}
              >
                {titleToRender}
              </h2>
            )}
            {shouldRenderSubtitle && (
              <p
                className={cx(
                  styles.linkMapSubtitle,
                  'text-md-up-04 text-weight-medium mt-md-1',
                  {
                    'opacity-5': isEditMode && !hasSubtitle,
                  },
                )}
              >
                {subtitleToRender}
              </p>
            )}
            <div
              className="d-flex flex-column gap-4x mt-4 mt-md-5x"
              aria-live="polite"
            >
              <div className="d-flex flex-column gap-3x">
                <BRSelect
                  id={selectId}
                  // label={intl.formatMessage(messages.selectPlaceholder)}
                  placeholder={intl.formatMessage(messages.selectPlaceholder)}
                  options={selectOptions}
                  value={selectedStateCode}
                  onChange={handleSelectChange}
                  disabled={!linkedStates.length}
                  className="link-map__select"
                  mode={useDarkTheme ? 'dark' : undefined}
                />
                {!linkedStates.length && (
                  <p
                    className={cx(
                      'link-map__empty-message',
                      styles.linkMapEmptyMessage,
                    )}
                  >
                    {intl.formatMessage(messages.emptyStateMessage)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <figure
            className={cx(
              'link-map__figure',
              'd-flex justify-content-center align-items-center',
              styles.linkMapFigure,
              {
                'is-edit-mode': isEditMode,
              },
            )}
          >
            <Map
              accessibleLabel={accessibleLabel}
              fallbackAlt={fallbackAlt}
              isEditMode={isEditMode}
              stateLinks={stateLinks}
              activeStateCode={highlightedState}
              onStateHover={handleStateHover}
              onStateLeave={handleStateLeave}
            />
          </figure>
        </div>
      </div>
    </div>
  );
};

LinkMapBody.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    linkTemplate: PropTypes.string,
    linkFormat: PropTypes.string,
    searchAcrossPortal: PropTypes.bool,
    useFullWidth: PropTypes.bool,
    useDarkTheme: PropTypes.bool,
    ...STATE_PROP_TYPES,
  }),
  isEditMode: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default LinkMapBody;
